import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { GuestPaymentSuccessView } from '@pillar-portal/api-types';
import { ApiError, paymentsApi } from '@/lib/pillarApi';
import { Button } from '@/components/ui/button';

function resolveErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message.trim().length > 0) {
        return error.message;
    }
    return 'Payment success details are unavailable.';
}

function resolveAccountCtaLabel(view: GuestPaymentSuccessView): string {
    if (view.account_cta === undefined) {
        throw new Error('Account CTA is unavailable.');
    }
    return view.account_cta.kind === 'LOGIN'
        ? 'Sign in to view your payments'
        : 'Sign-up to keep track of your payments';
}

function ConfettiBackdrop() {
    const pieces = useMemo(
        () =>
            Array.from({ length: 18 }, (_, index) => ({
                id: index,
                left: `${(index * 17) % 100}%`,
                top: `${(index * 11) % 80}%`,
                rotate: `${(index * 29) % 360}deg`,
                color: ['#f97316', '#0f766e', '#eab308', '#0284c7'][index % 4],
            })),
        []
    );

    return (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
            {pieces.map((piece) => (
                <span
                    key={piece.id}
                    className="absolute block h-3 w-8 rounded-full opacity-70"
                    style={{
                        left: piece.left,
                        top: piece.top,
                        transform: `rotate(${piece.rotate})`,
                        backgroundColor: piece.color,
                    }}
                />
            ))}
        </div>
    );
}

export default function PaymentSuccessPage() {
    const successViewQuery = useQuery({
        queryKey: ['publicPaymentRequest', 'paymentSuccess'],
        queryFn: () => paymentsApi.getCurrentGuestPaymentSuccessView(),
        retry: (failureCount, error) =>
            error instanceof ApiError && error.status === 404 && failureCount < 40,
        retryDelay: 250,
    });

    if (successViewQuery.isPending) {
        return (
            <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
                <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                    <h1 className="text-2xl font-semibold text-slate-900">
                        Loading payment confirmation
                    </h1>
                    <p className="mt-2 text-sm text-slate-600">
                        Retrieving the completed payment details.
                    </p>
                </div>
            </main>
        );
    }

    if (successViewQuery.isError || successViewQuery.data === undefined) {
        return (
            <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
                <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                    <h1 className="text-2xl font-semibold text-slate-900">
                        Payment confirmation unavailable
                    </h1>
                    <p className="mt-2 text-sm text-rose-700">
                        {resolveErrorMessage(successViewQuery.error)}
                    </p>
                    <div className="mt-6">
                        <Button asChild>
                            <Link to="/pay">Return to payment page</Link>
                        </Button>
                    </div>
                </div>
            </main>
        );
    }

    const successView = successViewQuery.data;

    return (
        <main className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_#fef3c7,_#f8fafc_45%,_#e2e8f0)] p-6">
            <div className="relative w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
                <ConfettiBackdrop />
                <div className="relative">
                    <div className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-900">
                        Payment successful
                    </div>
                    <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-900">
                        Your payment was successful. Thank you!
                    </h1>
                    <p className="mt-3 text-base text-slate-600">
                        {successView.merchant_display_name} has received your payment.
                    </p>

                    <div className="mt-8 grid gap-4 md:grid-cols-2">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                            <div className="text-sm font-medium uppercase tracking-wide text-slate-500">
                                Gateway Transaction ID
                            </div>
                            <div className="mt-2 break-all text-lg font-semibold text-slate-900">
                                {successView.gateway_txn_id ?? 'Unavailable'}
                            </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                            <div className="text-sm font-medium uppercase tracking-wide text-slate-500">
                                Authorization Code
                            </div>
                            <div className="mt-2 break-all text-lg font-semibold text-slate-900">
                                {successView.gateway_auth_code ?? 'Unavailable'}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                        <Button asChild>
                            <a
                                href={successView.receipt_html_path ?? '#'}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Get Receipt
                            </a>
                        </Button>
                        {successView.account_cta !== undefined ? (
                            <Button
                                asChild
                                variant="outline"
                                className="border-primary bg-white text-primary hover:bg-primary/5 hover:text-primary"
                            >
                                <Link to={successView.account_cta.href}>
                                    {resolveAccountCtaLabel(successView)}
                                </Link>
                            </Button>
                        ) : null}
                    </div>
                </div>
            </div>
        </main>
    );
}
